import BootcampDetailPage from "layouts/bootcamps/BootcampDetailPage";
import PaymentManagement from "layouts/payment-management";
import Timesheet from "layouts/timesheet";
import PaymentLinkGenerator from "layouts/payment-link-generator";
import PaymentPlans from "layouts/PaymentPlans";

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import BlogCategories from "layouts/blog-categories";
import BlogPosts from "layouts/blog-posts";
import Services from "layouts/services";
import Bootcamps from "layouts/bootcamps";
import TeamMembers from "layouts/team-members";
import Testimonials from "layouts/testimonials";
import Pages from "layouts/pages";
import Settings from "layouts/settings";
import ContactSubmissions from "layouts/contact-submissions";
import LandingApplications from "layouts/landing-applications";
import WebinarRegistrations from "layouts/webinar-registrations";
import Autoresponder from "layouts/autoresponder";
import FileManager from "layouts/file-manager";
import BootcampApplications from "layouts/bootcamp-applications";
import Consultations from "layouts/consultations";
import ClarityCallBookings from "layouts/clarity-call-bookings";
import ClarityCallAvailability from "layouts/clarity-call-availability";
import Meetings from "layouts/meetings";
import Newsletter from "layouts/newsletter";
import BulkMessage from "layouts/bulk-message";
import CustomerChat from "layouts/customer-chat";
import Calls from "layouts/calls";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import AcceptInvite from "layouts/authentication/accept-invite";
import Logout from "layouts/logout";
import PrivateRoute from "components/PrivateRoute";

// @mui icons
import Icon from "@mui/material/Icon";

import ManageUsers from "layouts/manage-users";

const routes = [
  // ── Dashboard ──────────────────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: (
      <PrivateRoute>
        <Dashboard />
      </PrivateRoute>
    ),
    description: "Overview & Analytics",
  },

  // ── Payments ───────────────────────────────────────────────────────────────
  {
    type: "divider",
    key: "divider-payments",
  },
  {
    type: "title",
    title: "Payments",
    key: "payments-title",
  },
  {
    type: "collapse",
    name: "Payment Management",
    key: "payment-management",
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/payment-management",
    component: (
      <PrivateRoute permission="payment-management">
        <PaymentManagement />
      </PrivateRoute>
    ),
    description: "Manage payments, refunds & exports",
  },
  {
    type: "collapse",
    name: "Generate Payment Link",
    key: "payment-link-generator",
    icon: <Icon fontSize="small">link</Icon>,
    route: "/payment-link-generator",
    component: (
      <PrivateRoute permission="payment-link-generator">
        <PaymentLinkGenerator />
      </PrivateRoute>
    ),
    description: "Create & send payment links via email & SMS",
  },

  // ── Blog ───────────────────────────────────────────────────────────────────
  {
    type: "divider",
    key: "divider-blog",
  },
  {
    type: "title",
    title: "Blog Management",
    key: "blog-title",
  },
  {
    type: "collapse",
    name: "Blog Categories",
    key: "blog-categories",
    icon: <Icon fontSize="small">category</Icon>,
    route: "/blog-categories",
    component: (
      <PrivateRoute permission="blog-categories">
        <BlogCategories />
      </PrivateRoute>
    ),
    description: "Manage blog categories",
  },
  {
    type: "collapse",
    name: "Blog Posts",
    key: "blog-posts",
    icon: <Icon fontSize="small">article</Icon>,
    route: "/blog-posts",
    component: (
      <PrivateRoute permission="blog-posts">
        <BlogPosts />
      </PrivateRoute>
    ),
    description: "Create & edit posts",
  },

  // ── Content ────────────────────────────────────────────────────────────────
  {
    type: "divider",
    key: "divider-content",
  },
  {
    type: "title",
    title: "Content Management",
    key: "content-title",
  },
  {
    type: "collapse",
    name: "Services",
    key: "services",
    icon: <Icon fontSize="small">business_center</Icon>,
    route: "/services",
    component: (
      <PrivateRoute permission="services">
        <Services />
      </PrivateRoute>
    ),
    description: "Manage service offerings",
  },
  {
    type: "collapse",
    name: "Bootcamps",
    key: "bootcamps",
    icon: <Icon fontSize="small">school</Icon>,
    route: "/bootcamps",
    component: (
      <PrivateRoute permission="bootcamps">
        <Bootcamps />
      </PrivateRoute>
    ),
    description: "Training programs",
  },
  {
    // Detail page — same permission as the Bootcamps list
    type: "route",
    name: "Bootcamp Detail",
    key: "bootcamp-detail",
    route: "/bootcamps/:slug",
    component: (
      <PrivateRoute permission="bootcamps">
        <BootcampDetailPage />
      </PrivateRoute>
    ),
    description: "Bootcamp detail and payment",
  },
  {
    type: "collapse",
    name: "Team Members",
    key: "team-members",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/team-members",
    component: (
      <PrivateRoute permission="team-members">
        <TeamMembers />
      </PrivateRoute>
    ),
    description: "Manage team profiles",
  },
  {
    type: "collapse",
    name: "Testimonials",
    key: "testimonials",
    icon: <Icon fontSize="small">star</Icon>,
    route: "/testimonials",
    component: (
      <PrivateRoute permission="testimonials">
        <Testimonials />
      </PrivateRoute>
    ),
    description: "Client reviews",
  },
  {
    type: "collapse",
    name: "Website Pages",
    key: "pages",
    icon: <Icon fontSize="small">description</Icon>,
    route: "/pages",
    component: (
      <PrivateRoute permission="pages">
        <Pages />
      </PrivateRoute>
    ),
    description: "Custom page content",
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: (
      <PrivateRoute permission="settings">
        <Settings />
      </PrivateRoute>
    ),
    description: "Site configuration",
  },

  // ── Submissions / Leads ────────────────────────────────────────────────────
  {
    type: "divider",
    key: "divider-submissions",
  },
  {
    type: "title",
    title: "Submissions & Leads",
    key: "submissions-title",
  },
  {
    type: "collapse",
    name: "Autoresponder",
    key: "autoresponder",
    icon: <Icon fontSize="small">auto_awesome</Icon>,
    route: "/autoresponder",
    component: (
      <PrivateRoute permission="autoresponder">
        <Autoresponder />
      </PrivateRoute>
    ),
    description: "Drip email/WhatsApp/SMS campaigns",
  },
  {
    type: "collapse",
    name: "Bulk Message",
    key: "bulk-message",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/bulk-message",
    component: (
      <PrivateRoute permission="autoresponder">
        <BulkMessage />
      </PrivateRoute>
    ),
    description: "Send bulk email or SMS with {{placeholders}}",
  },
  {
    type: "collapse",
    name: "Customer Chat",
    key: "customer-chat",
    icon: <Icon fontSize="small">forum</Icon>,
    route: "/customer-chat",
    component: (
      <PrivateRoute permission="customer-chat">
        <CustomerChat />
      </PrivateRoute>
    ),
    description: "Reply to inbound SMS & WhatsApp messages",
  },
  {
    type: "collapse",
    name: "Calls",
    key: "voice-calls",
    icon: <Icon fontSize="small">call</Icon>,
    route: "/calls",
    component: (
      <PrivateRoute permission="voice-calls">
        <Calls />
      </PrivateRoute>
    ),
    description: "Call history, missed calls, and voicemail",
  },
  {
    type: "collapse",
    name: "File Library",
    key: "file-manager",
    icon: <Icon fontSize="small">folder_open</Icon>,
    route: "/file-manager",
    component: (
      <PrivateRoute permission="file-manager">
        <FileManager />
      </PrivateRoute>
    ),
    description: "Upload files, use {{file:slug}} anywhere",
  },
  {
    type: "collapse",
    name: "Webinar Registrations",
    key: "webinar-registrations",
    icon: <Icon fontSize="small">live_tv</Icon>,
    route: "/webinar-registrations",
    component: (
      <PrivateRoute permission="webinar-registrations">
        <WebinarRegistrations />
      </PrivateRoute>
    ),
    description: "Webinar sign-ups",
  },
  {
    type: "collapse",
    name: "Landing Applications",
    key: "landing-applications",
    icon: <Icon fontSize="small">how_to_reg</Icon>,
    route: "/landing-applications",
    component: (
      <PrivateRoute permission="landing-applications">
        <LandingApplications />
      </PrivateRoute>
    ),
    description: "Landing page form submissions",
  },
  {
    type: "collapse",
    name: "Contact Submissions",
    key: "contact-submissions",
    icon: <Icon fontSize="small">email</Icon>,
    route: "/contact-submissions",
    component: (
      <PrivateRoute permission="contact-submissions">
        <ContactSubmissions />
      </PrivateRoute>
    ),
    description: "Inquiries & messages",
  },
  {
    type: "collapse",
    name: "Consultations",
    key: "consultations",
    icon: <Icon fontSize="small">event</Icon>,
    route: "/consultations",
    component: (
      <PrivateRoute permission="consultations">
        <Consultations />
      </PrivateRoute>
    ),
    description: "Consultation bookings",
  },
  {
    type: "collapse",
    name: "Clarity Call Bookings",
    key: "clarity-call-bookings",
    icon: <Icon fontSize="small">support_agent</Icon>,
    route: "/clarity-call-bookings",
    component: (
      <PrivateRoute permission="clarity-call-bookings">
        <ClarityCallBookings />
      </PrivateRoute>
    ),
    description: "Clarity call bookings from webinar funnels",
  },
  {
    // Availability rules editor — reached from the Clarity Call Bookings header, same permission
    type: "route",
    name: "Clarity Call Availability",
    key: "clarity-call-availability",
    route: "/clarity-call-bookings/availability",
    component: (
      <PrivateRoute permission="clarity-call-bookings">
        <ClarityCallAvailability />
      </PrivateRoute>
    ),
    description: "Bookable hours for Clarity Calls",
  },
  {
    type: "collapse",
    name: "Meetings",
    key: "meetings",
    icon: <Icon fontSize="small">video_camera_front</Icon>,
    route: "/meetings",
    component: (
      <PrivateRoute permission="meetings">
        <Meetings />
      </PrivateRoute>
    ),
    description: "Create Teams meetings for clients and follow-ups",
  },
  {
    type: "collapse",
    name: "Bootcamp Applications",
    key: "bootcamp-applications",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/bootcamp-applications",
    component: (
      <PrivateRoute permission="bootcamp-applications">
        <BootcampApplications />
      </PrivateRoute>
    ),
    description: "Program enrollments",
  },
  {
    type: "collapse",
    name: "Newsletter",
    key: "newsletter",
    icon: <Icon fontSize="small">mail_outline</Icon>,
    route: "/newsletter",
    component: (
      <PrivateRoute permission="newsletter">
        <Newsletter />
      </PrivateRoute>
    ),
    description: "Manage subscribers & send emails",
  },

  // ── Account ────────────────────────────────────────────────────────────────
  {
    type: "divider",
    key: "divider-account",
  },
  {
    type: "collapse",
    name: "Timesheet",
    key: "timesheet",
    icon: <Icon fontSize="small">schedule</Icon>,
    route: "/timesheet",
    component: (
      <PrivateRoute>
        <Timesheet />
      </PrivateRoute>
    ),
    description: "Log daily work hours and generate reports",
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: (
      <PrivateRoute>
        <Profile />
      </PrivateRoute>
    ),
    description: "Your account details",
  },
  {
    // Global-admin only — hasPermission("manage-users") returns false for non-global-admins
    type: "collapse",
    name: "Manage Users",
    key: "manage-users",
    icon: <Icon fontSize="small">manage_accounts</Icon>,
    route: "/manage-users",
    component: (
      <PrivateRoute permission="manage-users">
        <ManageUsers />
      </PrivateRoute>
    ),
    description: "User management & permissions",
  },
  {
    type: "collapse",
    name: "Logout",
    key: "logout",
    icon: <Icon fontSize="small">logout</Icon>,
    route: "/logout",
    component: (
      <PrivateRoute>
        <Logout />
      </PrivateRoute>
    ),
    description: "Sign out securely",
  },

  // ── Public ─────────────────────────────────────────────────────────────────
  {
    type: "route-only",
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "route-only",
    key: "accept-invite",
    route: "/authentication/accept-invite",
    component: <AcceptInvite />,
  },
];

export default routes;
